// ADR-110: DOM-to-image capture with pre-rasterize masking.
//
// Deliberately NOT using navigator.mediaDevices.getDisplayMedia() — see
// docs/ADR-110-support-ticketing-system.md "فرآیند طراحی" for why: that
// API only yields already-rasterized pixels (no chance to mask before
// rasterization) and shows a browser tab-picker dialog, so we could never
// be certain the captured tab was actually this platform tab.
//
// html2canvas is used instead: it walks the live DOM of the current page,
// so we can inject solid-box overlays over every element carrying
// data-ticket-mask="solid" BEFORE html2canvas rasterizes, guaranteeing
// masked content is never rendered into the output image (and therefore
// never sent to the backend — Rule TCK-02).

import html2canvas from "html2canvas";

const MASK_SELECTOR = '[data-ticket-mask="solid"]';

interface OverlayHandle {
  el: HTMLDivElement;
}

function applyMaskOverlays(root: HTMLElement): OverlayHandle[] {
  const targets = Array.from(root.querySelectorAll<HTMLElement>(MASK_SELECTOR));
  const overlays: OverlayHandle[] = [];

  for (const target of targets) {
    const rect = target.getBoundingClientRect();
    const overlay = document.createElement("div");
    overlay.setAttribute("data-ticket-mask-overlay", "true");
    Object.assign(overlay.style, {
      position: "fixed",
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      backgroundColor: "#000000",
      zIndex: "2147483647",
      pointerEvents: "none",
    } as CSSStyleDeclaration);
    document.body.appendChild(overlay);
    overlays.push({ el: overlay });
  }

  return overlays;
}

function removeMaskOverlays(overlays: OverlayHandle[]): void {
  for (const { el } of overlays) {
    el.remove();
  }
}

/**
 * Captures the current document as a masked PNG data URL.
 * Guarantees: the raw (unmasked) frame is never persisted or transmitted —
 * overlays are painted before html2canvas rasterizes, and are removed
 * immediately after, all synchronously within this function.
 */
export async function captureTicketScreenshot(): Promise<Blob> {
  const overlays = applyMaskOverlays(document.body);
  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
    });
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob returned null"));
      }, "image/png");
    });
  } finally {
    // Always remove overlays, even on failure, so we never leave the live
    // page visually altered for the user.
    removeMaskOverlays(overlays);
  }
}
